import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";

import { radialTransforms, StackedBlur } from "./stacked-blur.tsx";
import { isTransitionIdle } from "./transition-idle.ts";
import { useStackedBlur, useTransitionBlur } from "./use-transition-blur.ts";

export type ZoomOutBlurProps = {
  blur?: number;
  zoom?: number;
};

const clampOpts = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

// The exiting scene streaks in place without zooming; the entering scene zooms out and settles.
const EXIT_END = 0.3;
const ENTER_START = 0.12;
const ENTER_OPAQUE = 0.32;
const MIN_INTENSITY = 0.01;

const RADIAL_COPIES = 8;
const RADIAL_SPREAD = 0.25;
const RADIAL_GAUSSIAN = 0.4;

const ZoomOutBlurPresentation: React.FC<
  TransitionPresentationComponentProps<ZoomOutBlurProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const { blur: fullBlur = 24, zoom = 1.6 } = passedProps;
  const blur = useTransitionBlur(fullBlur);
  const stacked = useStackedBlur();
  const entering = presentationDirection === "entering";
  const p = presentationProgress;

  if (isTransitionIdle(presentationDirection, p)) {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  // Fast at first, then a long slow settle.
  const settle = interpolate(p, [ENTER_START, 1], [0, 1], clampOpts);
  const raw = entering
    ? (1 - settle) ** 4
    : interpolate(p, [0, EXIT_END], [0, 1], clampOpts) ** 2;
  const opacity = entering
    ? interpolate(p, [ENTER_START, ENTER_OPAQUE], [0, 1], clampOpts)
    : 1;
  // The opaque entering scene covers the exiting one.
  const covered = !entering && p >= ENTER_OPAQUE;
  const intensity = opacity === 0 || covered || raw < MIN_INTENSITY ? 0 : raw;
  // The oversized copies also cover the edges a plain blur would fade to black.
  const streaks = stacked && intensity > 0;

  const style: React.CSSProperties = {
    opacity,
    transform: entering
      ? `scale(${zoom + (1 - zoom) * Easing.out(Easing.poly(5))(settle)})`
      : undefined,
    filter:
      intensity > 0
        ? `blur(${intensity * blur * (streaks ? RADIAL_GAUSSIAN : 1)}px)`
        : undefined,
  };

  return (
    <AbsoluteFill style={style}>
      {streaks ? (
        <StackedBlur
          transforms={radialTransforms(
            RADIAL_SPREAD * intensity,
            RADIAL_COPIES,
          )}
        >
          {children}
        </StackedBlur>
      ) : (
        children
      )}
    </AbsoluteFill>
  );
};

export function zoomOutBlur(
  props: ZoomOutBlurProps = {},
): TransitionPresentation<ZoomOutBlurProps> {
  return {
    component: ZoomOutBlurPresentation,
    props,
  };
}
